<template>
	<div v-if="isOpen" class="modal-overlay" @click="closeModal">
		<form class="modal-content" @click.stop @submit.prevent="onSave">
			<div class="modal-header">
				<h3>{{ label("title") }}</h3>
				<button type="button" class="close-btn" :disabled="saving" @click="closeModal">&times;</button>
			</div>

			<div class="modal-body">
				<div v-if="submitError" class="modal-error">{{ submitError }}</div>

				<div class="field">
					<span class="field-label">{{ label("user") }}</span>
					<ArkListboxDropdown
						:model-value="userProfileId"
						@update:model-value="handleUserChange"
						:options="userOptions"
						:multiple="false"
						:disabled="saving"
						:clearable="false"
						:searchable="true"
						:placeholder="label('userPlaceholder')"
						:no-options-text="label('noUsers')"
						:same-width="true"
					/>
				</div>

				<label class="field">
					<span class="field-label">{{ label("message") }}</span>
					<textarea
						v-model.trim="message"
						class="field-input field-textarea"
						:placeholder="label('messagePlaceholder')"
						:disabled="saving"
						maxlength="320"
					></textarea>
				</label>

				<p class="character-count">{{ label("characterCount") }}: {{ message.length }}/320</p>
			</div>

			<div class="modal-footer">
				<Button shape="pill" variant="secondary" size="m" type="button" :disabled="saving" @click="closeModal">
					{{ label("cancel") }}
				</Button>
				<Button shape="pill" variant="primary" size="m" type="submit" :disabled="saving || !canSave">
					{{ saving ? label("sending") : label("send") }}
				</Button>
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import Button from "@/components/ui/Button.vue"

type UserOption = {
	value: number
	label: string
}

type SendSmsInput = {
	userProfileId: number
	message: string
}

interface Props {
	isOpen: boolean
	userOptions: UserOption[]
	saving?: boolean
	submitError?: string
	backendReady?: boolean
	translationKey?: string
}

interface Emits {
	(e: "close"): void
	(e: "save", payload: SendSmsInput): void
}

const props = withDefaults(defineProps<Props>(), {
	saving: false,
	submitError: "",
	backendReady: false,
	translationKey: "adminPanelPage.sendSmsModal",
})

const emit = defineEmits<Emits>()
const { t } = useI18n()

const userProfileId = ref<number | null>(null)
const message = ref("")

const canSave = computed(() => props.backendReady && userProfileId.value != null && message.value.trim() !== "")

const label = (key: string) => t(`${props.translationKey}.${key}`)

watch(
	() => props.isOpen,
	open => {
		if (open) {
			userProfileId.value = null
			message.value = ""
		}
	}
)

const handleUserChange = (value: string | number | null | (string | number | null)[]) => {
	const selected = Array.isArray(value) ? value[0] : value
	userProfileId.value = selected == null ? null : Number(selected)
}

const closeModal = () => {
	if (!props.saving) {
		emit("close")
	}
}

const onSave = () => {
	if (!canSave.value || userProfileId.value == null) return

	emit("save", {
		userProfileId: userProfileId.value,
		message: message.value.trim(),
	})
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: rgba(0, 0, 0, 0.5);
	z-index: 1000;
}

.modal-content {
	width: 90%;
	max-width: 520px;
	max-height: 86vh;
	overflow-y: auto;
	border-radius: 8px;
	background: white;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 20px;
	border-bottom: 1px solid #e0e0e0;
	background-color: #f8f9fa;

	h3 {
		margin: 0;
		color: #333;
	}

	.close-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 30px;
		height: 30px;
		padding: 0;
		border: none;
		background: none;
		color: #666;
		cursor: pointer;
		font-size: 24px;
	}
}

.modal-body {
	display: grid;
	gap: 16px;
	padding: 20px;

	.modal-error {
		padding: 10px 12px;
		border: 1px solid #ffcdd2;
		border-radius: 4px;
		background-color: #ffebee;
		color: #d32f2f;
		font-size: 14px;
	}
}

.field {
	display: grid;
	gap: 6px;

	.field-label {
		color: #333;
		font-weight: 600;
	}

	.field-input {
		width: 100%;
		padding: 10px 12px;
		border: 1px solid #ccc;
		border-radius: 4px;
		font-size: 14px;

		&:focus {
			border-color: #1976d2;
			box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
			outline: none;
		}
	}

	.field-textarea {
		min-height: 128px;
		resize: vertical;
	}
}

.character-count {
	margin: -8px 0 0;
	color: var(--neutral-600);
	font-size: 13px;
	text-align: right;
}

.modal-footer {
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	padding: 20px;
	border-top: 1px solid #e0e0e0;
	background-color: #f8f9fa;
}
</style>
